import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse, Warehouse, WarehouseVm } from '@/api/types'

function unwrapData<T>(payload: any): T {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data as T
    if ('data' in payload) return payload.data as T
  }
  return payload as T
}

function mapWarehouse(item: any): Warehouse {
  return {
    id: Number(item?.id ?? item?.warehouseId ?? 0),
    code: String(item?.code ?? item?.warehouseCode ?? ''),
    name: String(item?.name ?? item?.warehouseName ?? ''),
    location: String(item?.location ?? ''),
    isTemperatureControlled: Boolean(item?.isTemperatureControlled ?? item?.temperatureControlled ?? false),
    // Manager/Head fields can come from different backend properties.
    // Prefer explicit employee fields when available.
    email: String(item?.headEmpEmail ?? item?.empEmail ?? item?.email ?? ''),
    empId: Number(
      item?.empId ??
        item?.employeeId ??
        item?.managerEmpId ??
        item?.warehouseManagerId ??
        item?.headEmpId ??
        item?.warehouseHeadEmpId ??
        0
    ),
    wareHouseManagerName: String(
      item?.headEmpName ??
        item?.wareHouseManagerName ??
        item?.warehouseManagerName ??
        item?.managerName ??
        ''
    ),
    contactNumber: String(
      item?.headEmpMobileNo ??
        item?.empMobileNo ??
        item?.contactNumber ??
        item?.contact ??
        item?.phoneNo ??
        ''
    ),
    operationalHours: String(item?.operationalHours ?? item?.operatingHours ?? ''),
    isWarehouseDistribution: Boolean(item?.isWarehouseDistribution ?? item?.warehouseDistribution ?? false),
    branchId: Number(item?.branchId ?? 0),
    city: Number(item?.city ?? item?.cityId ?? 0),
    country: Number(item?.country ?? item?.countryId ?? 0),
  }
}

export class WarehouseService {
  async getAllWarehouses(branchId: number): Promise<Warehouse[]> {
    const url = endpoints.warehouse.getAll(branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = unwrapData<any>(response.data)

    const arr = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.items)
          ? payload.items
          : []

    return arr.map(mapWarehouse)
  }

  async getWarehouseById(id: number): Promise<Warehouse> {
    const url = endpoints.warehouse.getById(id)
    const response = await apiClient.get<ApiResponse<any> | any>(url)
    const payload = unwrapData<any>(response.data)
    return mapWarehouse(payload)
  }

  async createWarehouse(vm: WarehouseVm): Promise<Warehouse> {
    const url = endpoints.warehouse.create
    const response = await apiClient.post<ApiResponse<any> | any>(url, vm)
    const payload = unwrapData<any>(response.data)
    return mapWarehouse(payload)
  }

  async updateWarehouse(vm: WarehouseVm): Promise<Warehouse> {
    const url = endpoints.warehouse.update
    const response = await apiClient.put<ApiResponse<any> | any>(url, vm)
    const payload = unwrapData<any>(response.data)
    return mapWarehouse(payload)
  }

  async deleteWarehouse(id: number): Promise<void> {
    const url = endpoints.warehouse.delete(id)
    await apiClient.delete(url)
  }
}

export const warehouseService = new WarehouseService()

